import './RoomChatting.scoped.scss'
import PlainMessage from './PlainMessage';
import { useRecoilState } from 'recoil';
import participantsState from '../../../atoms/participants';
import userDetailsState from '../../../atoms/userDetails';
import messagesState from '../../../atoms/messages';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import emitSendMessage from '../../../connector/emits/sendMessage';
import { getUser } from '../../../connector';
import { MessageType, SystemMessageType } from '../../../connector/type';
import emitCleanRoom from '../../../connector/emits/cleanRoom';
import myInfoState from '../../../atoms/myinfo';
import roomDetailState, { ProjectStatus, kickState } from '../../../atoms/roomDetail';
import SystemMessage from './SystemMessage';
import emitGiftBullet from '../../../connector/emits/giftBullet';
import PickComponent from './Pick';
import ProjectSetting from './ProjectSetting';
import { openAlert, openConfirm, openInputbox } from '../../../atoms/common';
import ChangeGameType from './ChangeGameType';
import emitChangeRoomType from '../../../connector/emits/changeRoomType';
import emitRecommendRoom from '../../../connector/emits/recommendRoom';
import emitFreezeAll from '../../../connector/emits/freezeAll';
import emitUnfreezeAll from '../../../connector/emits/unfreezeAll';
import emitRenameRoom from '../../../connector/emits/renameRoom';

const infinity = Math.pow(2, 31);

function getScrolledPercent(element: HTMLElement) {
  if (element == null) {
    return 0;
  }
  const scrollRange = element.scrollHeight - element.offsetHeight;
  return Math.round((element.scrollTop / scrollRange) * 100);
}

function RoomChatting() {
  // Recoil
  const [roomDetail] = useRecoilState(roomDetailState);
  const [myInfo] = useRecoilState(myInfoState);
  const [participants] = useRecoilState(participantsState);
  const [userDetails] = useRecoilState(userDetailsState);
  const [messages] = useRecoilState(messagesState);
  const [kick] = useRecoilState(kickState);

  // Local state
  const [hasFirstScrolled, setHasFirstScrolled] = useState(false);
  const [message, setMessage] = useState('');
  const [lastScrolledPercent, setLastScrolledPercent] = useState(0);
  const [isOpenedPick, setIsOpenedPick] = useState(false);
  const [isOpenedChangeGameType, setIsOpenedChangeGameType] = useState(false);
  const [isOpenedProjectSetting, setIsOpenedProjectSetting] = useState(false);
  const messagesReference = useRef<HTMLDivElement>(null);

  // Effect
  useEffect(() => {
    setTimeout(() => {
      if (messagesReference.current != null) {
        messagesReference.current.scrollTop =  messagesReference.current.scrollHeight;;
        setLastScrolledPercent(100);
      }
      messagesReference.current?.addEventListener('scroll', (event) => {
        if (messagesReference.current == null) {
          return;
        }
        setLastScrolledPercent(getScrolledPercent(messagesReference.current));
      });
    }, 500);
  }, []);

  useEffect(() => {
    if (messagesReference.current == null) {
      return;
    }
    if (!hasFirstScrolled && messages.messages.length > 0) {
      setHasFirstScrolled(true);
      messagesReference.current.scrollTop =  messagesReference.current.scrollHeight;;
      return;
    }
    if (lastScrolledPercent === 100) {
      messagesReference.current.scrollTop =  messagesReference.current.scrollHeight;;
    }
  }, [hasFirstScrolled, lastScrolledPercent, messages]);

  // Event
  const onKeyPressinput = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  // Handle
  const sendMessage = () => {
    if (message.length === 0) {
      return;
    }
    emitSendMessage(participants.roomId, {
      user: getUser(),
      messageType: MessageType.PLANE,
      contents: message,
    });
    setMessage('');
  }

  const cleanRoom = () => {
    emitCleanRoom(roomDetail.id);
  }

  const giftBullet = () => {
    openInputbox(
      `현재 보석 보유 개수: ${myInfo.bullet}\n방장에게 선물 할 보석의 개수를 입력해주세요 (숫자만 입력)`,
      (input) => {
        const quantity = Number(input);
        if (!isNaN(quantity)) {
          if (quantity <= 0) {
            openAlert('선물할 보석의 개수를 정상적으로 입력해주세요.');
          } else if (quantity > myInfo.bullet) {
            openAlert('선물할 보석의 개수가 부족합니다.');
          } else {
            emitGiftBullet(quantity);
          }
        } else {
          openAlert('정확한 숫자를 입력해 주시기 바랍니다.');
        }
      }
    );
  }

  const recommendRoom = () => {
    if (roomDetail.recommenders.includes(myInfo.id)) {
      openAlert('이미 추천한 방입니다.');
      return;
    }
    emitRecommendRoom(roomDetail.id);
  };

  // Build messageComponents
  const messageComponents = messages.roomId === participants.roomId
    ? messages.messages.map((message, index) => {
      if (message.messageType === MessageType.SYSTEM) {
        return (
          <SystemMessage
            key={index}
            userId={message.user} 
            userDetail={userDetails.get(message.user)}
            contents={message.contents as { systemMessageType: SystemMessageType }}
          />
        );
      }
      return (
        <PlainMessage 
          key={index}
          userId={message.user} 
          userDetail={userDetails.get(message.user)}
          contents={message.contents as string}
        />
      );
    })
    : null;

  const changeRoomType = async () => {
    if (!roomDetail.isProject) {
      openConfirm('정말 프리미엄 방으로 전환하시겠습니까?', () => {
        emitChangeRoomType(roomDetail.id);
      });
    } else {
      openConfirm('정말 일반 방으로 전환하시겠습니까?', () => {
        emitChangeRoomType(roomDetail.id);
      });
    }
  };

  const renameRoom = async () => {
    openInputbox(
      '변경 할 방 이름을 입력해주세요.',
      (input) => {
        console.info('inputboxInput', input);
        if (!input) {
          openAlert('방 이름은 공백일 수 없습니다.');
        } else {
          emitRenameRoom(roomDetail.id, input);
          openAlert('방 이름 변경이 완료되었습니다.');
        }
      },
    );
  };

  return (
    <div className="chatting">
      <div className="project-status" style={{ display: 'none' }}>
        {
          roomDetail.projectStatus === ProjectStatus.PLAY ? (
            <>
              <div>
                프리미엄 타입 <b>{roomDetail.project.projectType}</b>
              </div>
              <div>
                💰참여비 <b>{`${roomDetail.project.projectPrice.toLocaleString()}보석`}</b>
              </div>
              <div>
                {/* 목표금액 <b>{`${Math.floor(roomDetail.project.projectTargetAmount / 10000).toLocaleString()}만`}</b> */}
                💰목표금액 <b>{`${roomDetail.project.projectTargetAmount.toLocaleString()}`}</b>
              </div>
              <div>
                {/* 현재금액 <b>{`${Math.floor(roomDetail.project.projectAmount / 10000).toLocaleString()}만`}</b> */}
                💰현재금액 <b>{`${roomDetail.project.projectAmount.toLocaleString()}`}</b>
              </div>
            </>
          ) : null
        }
        {
          roomDetail.projectStatus !== ProjectStatus.PLAY && roomDetail.projectStatus !== ProjectStatus.NONE_PROJECT ? (
            <>
              <div>
                방장이 아직 프리미엄를 시작하지 않았습니다.
              </div>
            </>
          ) : null
        }
        {
          roomDetail.projectStatus === ProjectStatus.NONE_PROJECT ? (
            <>
              <div>
                게임 타입 <b>{roomDetail.projectType}</b>
              </div>
            </>
          ) : null
        }
      </div>
      <div 
        className="chatting-messages"
        ref={messagesReference}
      >
        {
          messageComponents
        }
      </div>
     <div className='chatting-buttons'>
        {
          roomDetail.owner === myInfo.id
            ? (
              <>
                <button onClick={cleanRoom}>🧹방청소</button>
                <button onClick={roomDetail.isAllFreezing ? emitUnfreezeAll : emitFreezeAll}>🧊{roomDetail.isAllFreezing ? '얼리기해제' : '얼리기'}</button>
                {/* {
                  roomDetail.isProject === true && roomDetail.projectStatus !== ProjectStatus.PLAY ? (
                    <button
                      className="float-right"
                      onClick={() => setIsOpenedProjectSetting(!isOpenedProjectSetting)}
                    >
                      { isOpenedProjectSetting ? '프리미엄 설정 닫기' : '프리미엄 설정 열기' }
                    </button>
                  ) : null
                } */}
                <button
                  className="float-right"
                  onClick={() => setIsOpenedPick(!isOpenedPick)}
                >
                  { isOpenedPick ? '픽 닫기' : '픽 열기' }
                </button>
                {/* {
                  roomDetail.isProject === false || roomDetail.projectStatus === ProjectStatus.PLAY ? (
                    <button
                      className="float-right"
                      onClick={() => setIsOpenedChangeGameType(!isOpenedChangeGameType)}
                    >
                      { isOpenedChangeGameType ? '게임타입 변경 닫기' : '게임타입 변경 열기' }
                    </button>
                  ) : null
                } */}

                {
                  <button
                    className="float-right"
                    onClick={() => renameRoom()}
                  >
                    방 이름 변경
                  </button>
                }
              </>
            )
            : (
              <>
                <button onClick={giftBullet}>🪙보석 선물하기</button>
                <button onClick={recommendRoom}>👍방 좋아요</button>
                {
                  roomDetail.managers.includes(myInfo.id) ? (
                    <>
                      <button onClick={cleanRoom}>🧹방청소</button>
                      <button onClick={roomDetail.isAllFreezing ? emitUnfreezeAll : emitFreezeAll}>🧊{roomDetail.isAllFreezing ? '얼리기해제' : '얼리기'}</button>
                    </>
                  ) : null
                }
              </>
            )
        }
        {/* <button onClick={() => {}}>추천하기</button>
        <button onClick={() => {}}>사운드 끄기</button>
        <button onClick={() => {}}>이모티콘</button>
        <button onClick={() => {}}>즐겨찾기</button> */}
      </div>
      <div className="chatting-input">
        <input 
          type="text" 
          placeholder={
            myInfo.id === 'admin' || roomDetail.owner === myInfo.id || roomDetail.managers.includes(myInfo.id) ? '내용 입력'
            : kick === 'F' ? '벙어리 상태에는 채팅을 입력할 수 없습니다.'
            : roomDetail.isAllFreezing ? '현재 채팅방이 얼려있습니다.'
            : '내용 입력'
          }
          disabled={!(myInfo.id === 'admin' || roomDetail.owner === myInfo.id || roomDetail.managers.includes(myInfo.id)) && (kick === 'F' || roomDetail.isAllFreezing)}
          id="chat_input" 
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={onKeyPressinput}
          autoComplete="off"
        />
        <button onClick={sendMessage}></button>
      </div>
      {
        isOpenedPick
        ? <PickComponent />
        : null
      }
      {
        isOpenedChangeGameType
        ? <ChangeGameType />
        : null
      }
      {
        isOpenedProjectSetting && roomDetail.projectStatus !== ProjectStatus.PLAY
        ? <ProjectSetting />
        : null
      }
    </div>
  );
}

export default RoomChatting;